import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useGame } from '../contexts/GameContext';
import { t } from '../i18n/translations';
import { getTop5Scores, getSettings, getAsset, getCustomSpriteKeys } from '../services/api';
import { Globe, Settings, Volume2, VolumeX } from 'lucide-react';

const AttractScreen = () => {
  const { language, toggleLanguage, navigateTo, setSettings, settings } = useGame();
  const [topScores, setTopScores] = useState([]);
  const [logoTapCount, setLogoTapCount] = useState(0);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [audioEnabled, setAudioEnabled] = useState(() => localStorage.getItem('palletman_muted') !== '1');
  const audioEnabledRef = useRef(audioEnabled);
  const [customSprites, setCustomSprites] = useState({});
  const [dataLoaded, setDataLoaded] = useState(false);
  const attractAudio = useRef(null);
  const audioReady = useRef(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const spriteUrl = (spriteKey, defaultPath) => {
    if (!dataLoaded) return ''; // Prevent flash of default sprite
    return customSprites[spriteKey] ? `${backendUrl}${customSprites[spriteKey]}` : defaultPath;
  };

  const loadData = useCallback(async () => {
    try {
      const [scores, settingsData, customKeys] = await Promise.all([
        getTop5Scores(),
        getSettings(),
        getCustomSpriteKeys().catch(() => ({})),
      ]);
      setTopScores(scores);
      setSettings(settingsData);
      setCustomSprites(customKeys || {});
      setDataLoaded(true);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  }, [setSettings]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 10000);
    return () => clearInterval(interval);
  }, [loadData]);

  // Load attract audio asset
  useEffect(() => {
    let cancelled = false;
    let unlockFn = null;

    async function loadAttractAudio() {
      try {
        const asset = await getAsset('audio_attract');
        if (cancelled || !asset?.data || !asset?.mime_type) return;
        const audio = new Audio(`data:${asset.mime_type};base64,${asset.data}`);
        audio.loop = true;
        audio.volume = settings?.volume_music ?? 0.4;
        audio.preload = 'auto';
        attractAudio.current = audio;
        audioReady.current = true;

        // Only autoplay if audio is enabled
        if (!audioEnabledRef.current) return;

        // Try autoplay (usually blocked by browser policy)
        audio.play().catch(() => {
          if (cancelled) return;
          // Autoplay blocked — play on first user interaction
          unlockFn = () => {
            if (attractAudio.current && audioEnabledRef.current) {
              attractAudio.current.play().catch(() => {});
            }
          };
          window.addEventListener('pointerdown', unlockFn, { once: true });
        });
      } catch (e) {
        console.warn('Attract audio not available:', e);
      }
    }

    loadAttractAudio();
    return () => {
      cancelled = true;
      if (unlockFn) window.removeEventListener('pointerdown', unlockFn);
    };
  }, []);

  // Update volume from settings
  useEffect(() => {
    if (attractAudio.current && settings?.volume_music !== undefined) {
      attractAudio.current.volume = settings.volume_music;
    }
  }, [settings]);

  // Cleanup: stop audio on unmount
  useEffect(() => {
    return () => {
      if (attractAudio.current) {
        attractAudio.current.pause();
        attractAudio.current = null;
      }
    };
  }, []);

  const handleLogoTap = () => {
    const newCount = logoTapCount + 1;
    setLogoTapCount(newCount);
    
    if (newCount >= 5) {
      setShowPinModal(true);
      setLogoTapCount(0);
    }
    
    // Reset count after 2 seconds
    setTimeout(() => setLogoTapCount(0), 2000);
  };

  const handlePinSubmit = async () => {
    try {
      const { verifyAdminPin } = await import('../services/api');
      await verifyAdminPin(pin);
      setShowPinModal(false);
      setPin('');
      setPinError('');
      navigateTo('admin');
    } catch {
      setPinError('PIN non valido');
    }
  };

  const handleScreenTap = () => {
    navigateTo('form');
  };

  const toggleAudio = (e) => {
    e.stopPropagation();
    const next = !audioEnabled;
    setAudioEnabled(next);
    audioEnabledRef.current = next;
    localStorage.setItem('palletman_muted', next ? '0' : '1');
    if (attractAudio.current) {
      if (next) {
        attractAudio.current.volume = settings?.volume_music ?? 0.4;
        attractAudio.current.play().catch(() => {});
      } else {
        attractAudio.current.pause();
      }
    }
  };

  const attractBg = settings?.color_attract_bg || '#04082B';
  const accentColor = settings?.color_text_accent || '#FFD100';
  const lbHeader = settings?.color_leaderboard_header || '#005EB8';
  const btnColor = settings?.color_btn_play || '#FFD100';

  return (
    <div 
      className="flex flex-col items-center p-4 pt-14 pb-8 gap-6 relative"
      style={{ backgroundColor: attractBg, minHeight: '100dvh' }}
      onClick={handleScreenTap}
      data-testid="attract-screen"
    >
      {/* Scanlines overlay */}
      <div className="scanlines absolute inset-0 pointer-events-none" />
      
      {/* Language Toggle */}
      <button
        onClick={(e) => { e.stopPropagation(); toggleLanguage(); }}
        className="absolute top-4 right-4 z-20 flex items-center gap-2 px-3 py-2 bg-[#1e293b] rounded border border-[#475569] transition-colors"
        style={{ borderColor: undefined }}
        data-testid="language-toggle"
      >
        <Globe className="w-4 h-4 text-[#94a3b8]" />
        <span className="font-arcade text-xs" style={{ color: accentColor }}>{language.toUpperCase()}</span>
      </button>

      {/* Top-left controls: Audio Toggle + Admin */}
      <div className="absolute top-4 left-4 z-20 flex items-center gap-2">
        <button
          onClick={toggleAudio}
          className="flex items-center gap-2 px-3 py-2 bg-[#1e293b] rounded border border-[#475569] transition-colors"
          data-testid="audio-toggle"
        >
          {audioEnabled ? (
            <Volume2 className="w-4 h-4" style={{ color: accentColor }} />
          ) : (
            <VolumeX className="w-4 h-4 text-[#94a3b8]" />
          )}
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setShowPinModal(true); }}
          className="flex items-center gap-2 px-3 py-2 bg-[#1e293b] rounded border border-[#475569] transition-colors"
          data-testid="admin-button"
        >
          <Settings className="w-4 h-4 text-[#94a3b8]" />
          <span className="text-xs text-[#94a3b8]">Admin</span>
        </button>
      </div>

      {/* Banner */}
      <div 
        className="mt-8 cursor-pointer z-10"
        onClick={(e) => { e.stopPropagation(); handleLogoTap(); }}
        data-testid="logo-header"
      >
        <img src={dataLoaded ? (settings?.[`banner_${language}`] || `/api/banner/${language}`) : ''} alt="Mettiti alla Guida - Palletways" style={{ width: 500, opacity: dataLoaded ? 1 : 0, transition: 'opacity 0.3s' }} className="object-contain" />
      </div>

      {/* Title */}
      <div className="flex flex-col items-center">
        
        {/* Animated Enemies */}
        <div className="flex gap-8 mb-12 relative z-20" style={{ opacity: dataLoaded ? 1 : 0, transition: 'opacity 0.3s' }}>
          <div className="animate-float" style={{ animationDelay: '0s' }}>
            <img src={spriteUrl('red_ghost', '/assets/tiles/red_ghost.png')} alt="Blinky" className="w-14 h-14 object-contain drop-shadow-[0_0_8px_rgba(239,68,68,0.7)]" />
          </div>
          <div className="animate-float" style={{ animationDelay: '0.3s' }}>
            <img src={spriteUrl('blue_ghost', '/assets/tiles/blue_ghost.png')} alt="Inky" className="w-14 h-14 object-contain drop-shadow-[0_0_8px_rgba(59,130,246,0.7)]" />
          </div>
          <div className="animate-float" style={{ animationDelay: '0.6s' }}>
            <img src={spriteUrl('yellow_ghost', '/assets/tiles/yellow_ghost.png')} alt="Clyde" className="w-14 h-14 object-contain drop-shadow-[0_0_8px_rgba(249,115,22,0.7)]" />
          </div>
          <div className="animate-float" style={{ animationDelay: '0.9s' }}>
            <img src={spriteUrl('pink_ghost', '/assets/tiles/pink_ghost.png')} alt="Pinky" className="w-14 h-14 object-contain drop-shadow-[0_0_8px_rgba(236,72,153,0.7)]" />
          </div>
        </div>

        {/* Player Truck */}
        <div className="mb-8" style={{ opacity: dataLoaded ? 1 : 0, transition: 'opacity 0.3s' }}>
          <img src={spriteUrl('hero', '/assets/tiles/hero.png')} alt="Pallet Man" className="h-14 object-contain" />
        </div>

        {/* Tap to Start */}
        <p className="font-arcade text-sm sm:text-base animate-blink" style={{ color: btnColor }}>
          {settings?.[`tap_to_start_${language}`] || t('tapToStart', language)}
        </p>
      </div>

      {/* Leaderboard */}
      <div className="w-full max-w-md bg-[#1e293b] rounded-lg border-2 border-[#475569] overflow-hidden">
        <div className="px-4 py-2" style={{ backgroundColor: lbHeader }}>
          <h2 className="font-arcade text-xs text-center" style={{ color: accentColor }}>
            {t('highScores', language)}
          </h2>
        </div>
        <div className="divide-y divide-[#334155]">
          {topScores.length === 0 ? (
            <div className="p-4 text-center">
              <p className="font-arcade text-[10px] text-[#64748b]">NO SCORES YET</p>
            </div>
          ) : (
            topScores.map((score, index) => (
              <div key={score.id} className="leaderboard-entry">
                <span className="leaderboard-rank">{index + 1}.</span>
                <span className="leaderboard-initials">{score.initials}</span>
                <span className="leaderboard-score">{score.score.toLocaleString()}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* PIN Modal */}
      {showPinModal && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-[#1e293b] p-6 rounded-lg border-2 border-[#FFD100] w-80">
            <h3 className="font-arcade text-sm text-[#FFD100] mb-4 text-center">
              {t('admin.pinPrompt', language)}
            </h3>
            <input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              maxLength={4}
              className="w-full arcade-input text-center font-arcade text-2xl tracking-widest mb-4"
              autoFocus
              data-testid="admin-pin-input"
            />
            {pinError && (
              <p className="text-[#EF4444] text-xs text-center mb-4">{pinError}</p>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => { setShowPinModal(false); setPin(''); setPinError(''); }}
                className="flex-1 px-4 py-2 bg-[#475569] text-white rounded hover:bg-[#64748b] transition-colors"
              >
                {t('admin.cancel', language)}
              </button>
              <button
                onClick={handlePinSubmit}
                className="flex-1 px-4 py-2 bg-[#FFD100] text-black rounded hover:bg-[#fbbf24] transition-colors font-bold"
                data-testid="admin-pin-submit"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttractScreen;
